import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI } from '../../services/api';

const CATEGORIES = ['Appetizer', 'Main Course', 'Dessert', 'Beverages', 'Snacks', 'Sides'];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function AdminRestaurantManager() {
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('menu');
  const [loading, setLoading] = useState(false);

  // Menu state
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuCategory, setMenuCategory] = useState('');
  const [menuSearch, setMenuSearch] = useState('');
  const [showMenuForm, setShowMenuForm] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  // Category display-order editor (controls the section order in the customer app)
  const [allCategories, setAllCategories] = useState([]);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [showCatOrder, setShowCatOrder] = useState(false);
  const [catEdit, setCatEdit] = useState([]);
  const [catOrderSaving, setCatOrderSaving] = useState(false);
  const [menuForm, setMenuForm] = useState({ name: '', description: '', category: 'Main Course', price: '', isVegetarian: false, preparationTime: 20 });
  const menuImageRef = useRef(null);
  // Bulk add state (paste list -> web-scrape + classify -> preview/edit -> create)
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkGenImages, setBulkGenImages] = useState(false);
  const [bulkRows, setBulkRows] = useState(null); // null = not generated yet
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');

  // Coupon state
  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({ code: '', description: '', discountType: 'percentage', discountValue: '', minOrderValue: '', maxDiscount: '', validFrom: '', validUntil: '', usageLimit: '' });

  // Settings state
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Profile state
  const [profile, setProfile] = useState({ name: '', description: '', phone: '', email: '', address: {} });
  const [profileLoading, setProfileLoading] = useState(false);

  // Load restaurants
  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const { data } = await adminAPI.getAllRestaurants({ limit: 200 });
        if (data.success) {
          setRestaurants(data.data?.restaurants || data.data || []);
        }
      } catch (err) {
        console.error('Failed to load restaurants:', err);
      }
    };
    fetchRestaurants();
  }, []);

  const filteredRestaurants = restaurants.filter(r =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.ownerPhone?.includes(searchQuery) ||
    r.address?.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectRestaurant = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setActiveTab('menu');
  };

  // Load tab data when restaurant or tab changes
  useEffect(() => {
    if (!selectedRestaurant) return;
    const id = selectedRestaurant._id;
    if (activeTab === 'menu') loadMenu(id);
    else if (activeTab === 'coupons') loadCoupons(id);
    else if (activeTab === 'settings') loadSettings(id);
    else if (activeTab === 'profile') loadProfile(id);
  }, [selectedRestaurant, activeTab]);

  // ==========================================
  // MENU FUNCTIONS
  // ==========================================
  const loadMenu = async (restaurantId) => {
    setMenuLoading(true);
    try {
      const params = {};
      if (menuCategory) params.category = menuCategory;
      if (menuSearch) params.search = menuSearch;
      const { data } = await adminAPI.getRestaurantMenu(restaurantId, params);
      if (data.success) {
        setMenuItems(data.data.menuItems || []);
        setAllCategories(data.data.allCategories || []);
        setCategoryOrder(data.data.categoryOrder || []);
      }
    } catch (err) { console.error(err); }
    setMenuLoading(false);
  };

  // ---- Category display-order editor ----
  // Build the editable list: saved order (only categories that still exist),
  // then any remaining menu categories (alphabetical) appended.
  const openCatOrder = () => {
    const existing = allCategories;
    const savedExisting = categoryOrder.filter(c => existing.some(e => e.toLowerCase() === c.toLowerCase()));
    const savedKeys = new Set(savedExisting.map(c => c.toLowerCase()));
    const rest = existing
      .filter(e => !savedKeys.has(e.toLowerCase()))
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    setCatEdit([...savedExisting, ...rest]);
    setShowCatOrder(true);
  };
  const moveCat = (i, dir) => {
    setCatEdit(prev => {
      const arr = [...prev];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };
  const saveCategoryOrder = async () => {
    setCatOrderSaving(true);
    try {
      const { data } = await adminAPI.setRestaurantCategoryOrder(selectedRestaurant._id, catEdit);
      if (data.success) {
        setCategoryOrder(data.data.categoryOrder || catEdit);
        setShowCatOrder(false);
        alert('Category order saved — live in the customer app now.');
      }
    } catch (err) { alert('Failed to save category order'); }
    setCatOrderSaving(false);
  };

  const handleMenuSubmit = async (e) => {
    e.preventDefault();
    const id = selectedRestaurant._id;
    try {
      if (editingMenuItem) {
        await adminAPI.updateRestaurantMenuItem(id, editingMenuItem._id, menuForm);
        alert('Menu item updated');
      } else {
        const formData = new FormData();
        Object.entries(menuForm).forEach(([k, v]) => { if (v !== '' && v !== null) formData.append(k, v); });
        if (menuImageRef.current?.files[0]) formData.append('image', menuImageRef.current.files[0]);
        await adminAPI.addRestaurantMenuItem(id, formData);
        alert('Menu item added');
      }
      setShowMenuForm(false);
      setEditingMenuItem(null);
      setMenuForm({ name: '', description: '', category: 'Main Course', price: '', isVegetarian: false, preparationTime: 20 });
      loadMenu(id);
    } catch (err) { alert('Failed: ' + (err.response?.data?.message || err.message)); }
  };

  // ---- Bulk add (paste list -> web-scrape + classify -> preview/edit -> create) ----
  const runBulkScrape = async () => {
    if (!bulkText.trim()) return;
    setBulkBusy(true); setBulkMsg('');
    try {
      const { data } = await adminAPI.bulkScrapeMenuItems(selectedRestaurant._id, bulkText, bulkGenImages);
      if (data.success) setBulkRows(data.data.items);
    } catch (err) { setBulkMsg(err.response?.data?.message || 'Failed to generate preview'); }
    setBulkBusy(false);
  };
  const updateBulkRow = (idx, patch) => setBulkRows(rows => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  // Open this restaurant's OWN panel (the /restaurant/* portal) as the restaurant,
  // exactly as they see it after OTP login. Stashes the admin session so the
  // banner's "Exit to Admin" can restore it, then hard-reloads into the portal.
  const openRestaurantPanel = async (restaurant) => {
    if (!restaurant?._id) return;
    if (!window.confirm(`Open "${restaurant.name}" as the restaurant?\n\nYou will be viewing and acting AS this restaurant. Use "Exit to Admin" (top banner) to return to your admin panel.`)) return;
    try {
      const res = await adminAPI.impersonateRestaurant(restaurant._id);
      const data = res.data?.data || res.data;
      const token = data?.token;
      if (!token) throw new Error('No token returned');
      // Stash the current admin session for restore-on-exit.
      localStorage.setItem('impersonation_return_token', localStorage.getItem('token') || '');
      localStorage.setItem('impersonation_return_refresh', localStorage.getItem('refreshToken') || '');
      localStorage.setItem('impersonation_active', JSON.stringify({ restaurantName: data?.restaurant?.name || restaurant.name, at: Date.now() }));
      // Switch to the restaurant impersonation token. Drop the admin refresh token
      // so the API layer can't refresh back into the admin session while
      // impersonating (both are restored on exit).
      localStorage.setItem('token', token);
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      // Hard reload → AuthContext re-inits cleanly as the restaurant.
      window.location.href = '/restaurant/dashboard';
    } catch (e) {
      alert('Failed to open restaurant panel: ' + (e.response?.data?.message || e.message));
    }
  };
  const removeBulkRow = (idx) => setBulkRows(rows => rows.filter((_, i) => i !== idx));

  // Load the restaurant's WHOLE current menu into the same editable preview table, so the
  // admin can edit any item inline and save. Each row carries its _id → saved as an UPDATE
  // (not a duplicate). Fetches fresh with no category/search filter so nothing is missed.
  const loadCurrentMenuIntoBulk = async () => {
    if (!selectedRestaurant?._id) return;
    setBulkBusy(true); setBulkMsg('');
    try {
      const { data } = await adminAPI.getRestaurantMenu(selectedRestaurant._id);
      const items = data?.data?.menuItems || [];
      if (items.length === 0) { setBulkMsg('This restaurant has no menu items to load yet.'); setBulkBusy(false); return; }
      setBulkRows(items.map((m) => ({
        _id: m._id,
        existing: true,
        name: m.name || '',
        description: m.description || '',
        category: m.category || 'Main Course',
        isVegetarian: !!m.isVegetarian,
        price: m.price ?? '',
        variants: Array.isArray(m.variants) ? m.variants.map((v) => ({ name: v.name, price: v.price })) : [],
        availabilityTiming: {
          enabled: !!(m.availabilityTiming && m.availabilityTiming.enabled),
          startTime: (m.availabilityTiming && m.availabilityTiming.startTime) || '00:00',
          endTime: (m.availabilityTiming && m.availabilityTiming.endTime) || '23:59',
        },
        image: m.image || '',
        errors: [],
        alreadyExists: false,
        descriptionFound: true,
      })));
    } catch (err) { setBulkMsg(err.response?.data?.message || 'Failed to load current menu'); }
    setBulkBusy(false);
  };

  // A row is saveable if it has a name + price>0 AND is either an existing item (_id → update)
  // or a new item not already on the menu (→ create).
  const isBulkRowSaveable = (r) => !!r.name && Number(r.price) > 0 && (!!r._id || !r.alreadyExists);
  const bulkSaveCounts = () => {
    const rows = (bulkRows || []).filter(isBulkRowSaveable);
    return { updates: rows.filter((r) => r._id).length, creates: rows.filter((r) => !r._id).length };
  };
  const runBulkCreate = async () => {
    const valid = (bulkRows || []).filter(isBulkRowSaveable);
    if (valid.length === 0) { setBulkMsg('Nothing to save (each row needs a name + price > 0)'); return; }
    setBulkBusy(true); setBulkMsg('');
    try {
      const { data } = await adminAPI.bulkCreateMenuItems(selectedRestaurant._id, valid);
      if (data.success) {
        alert(data.message);
        setShowBulkPanel(false); setBulkRows(null); setBulkText(''); setBulkMsg('');
        loadMenu(selectedRestaurant._id);
      }
    } catch (err) { setBulkMsg(err.response?.data?.message || 'Failed to save items'); }
    setBulkBusy(false);
  };

  const deleteMenuItem = async (menuId) => {
    if (!window.confirm('Delete this menu item?')) return;
    try {
      await adminAPI.deleteRestaurantMenuItem(selectedRestaurant._id, menuId);
      loadMenu(selectedRestaurant._id);
    } catch (err) { alert('Failed to delete'); }
  };

  const toggleAvailability = async (menuId) => {
    try {
      await adminAPI.toggleRestaurantMenuAvailability(selectedRestaurant._id, menuId);
      loadMenu(selectedRestaurant._id);
    } catch (err) { alert('Failed'); }
  };

  const toggleOutOfStock = async (menuId) => {
    try {
      await adminAPI.toggleRestaurantMenuOutOfStock(selectedRestaurant._id, menuId);
      loadMenu(selectedRestaurant._id);
    } catch (err) { alert('Failed'); }
  };

  // Top Picks: patch enabled/badge/order for one item, then reload the list.
  const setTopPick = async (menuId, patch) => {
    try {
      await adminAPI.setRestaurantMenuTopPick(selectedRestaurant._id, menuId, patch);
      loadMenu(selectedRestaurant._id);
    } catch (err) { alert('Failed to update Top Pick'); }
  };

  // ==========================================
  // COUPON FUNCTIONS
  // ==========================================
  const loadCoupons = async (restaurantId) => {
    setCouponLoading(true);
    try {
      const { data } = await adminAPI.getRestaurantCoupons(restaurantId);
      if (data.success) setCoupons(data.data.coupons || []);
    } catch (err) { console.error(err); }
    setCouponLoading(false);
  };

  const handleCouponSubmit = async (e) => {
    e.preventDefault();
    const id = selectedRestaurant._id;
    try {
      if (editingCoupon) {
        await adminAPI.updateRestaurantCoupon(id, editingCoupon._id, couponForm);
        alert('Coupon updated');
      } else {
        await adminAPI.createRestaurantCoupon(id, couponForm);
        alert('Coupon created');
      }
      setShowCouponForm(false);
      setEditingCoupon(null);
      setCouponForm({ code: '', description: '', discountType: 'percentage', discountValue: '', minOrderValue: '', maxDiscount: '', validFrom: '', validUntil: '', usageLimit: '' });
      loadCoupons(id);
    } catch (err) { alert('Failed: ' + (err.response?.data?.message || err.message)); }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await adminAPI.deleteRestaurantCoupon(selectedRestaurant._id, couponId);
      loadCoupons(selectedRestaurant._id);
    } catch (err) { alert('Failed to delete'); }
  };

  const toggleCoupon = async (couponId) => {
    try {
      await adminAPI.toggleRestaurantCouponStatus(selectedRestaurant._id, couponId);
      loadCoupons(selectedRestaurant._id);
    } catch (err) { alert('Failed'); }
  };

  // ==========================================
  // SETTINGS FUNCTIONS
  // ==========================================
  const loadSettings = async (restaurantId) => {
    setSettingsLoading(true);
    try {
      const { data } = await adminAPI.getRestaurantSettings(restaurantId);
      if (data.success) setSettings(data.data);
    } catch (err) { console.error(err); }
    setSettingsLoading(false);
  };

  const saveSettings = async () => {
    try {
      await adminAPI.updateRestaurantSettings(selectedRestaurant._id, settings);
      alert('Settings saved');
    } catch (err) { alert('Failed: ' + (err.response?.data?.message || err.message)); }
  };

  // ==========================================
  // PROFILE FUNCTIONS
  // ==========================================
  const loadProfile = async (restaurantId) => {
    setProfileLoading(true);
    try {
      const { data } = await adminAPI.getRestaurantDetailsForReview(restaurantId);
      if (data.success) {
        const r = data.data?.restaurant || data.data;
        setProfile({
          name: r.name || '',
          description: r.description || '',
          phone: r.ownerId?.phone || r.ownerPhone || r.phone || '',
          email: r.ownerId?.email || r.ownerEmail || r.email || '',
          address: r.address || {},
          cuisineType: r.cuisineType || []
        });
      }
    } catch (err) { console.error(err); }
    setProfileLoading(false);
  };

  const saveProfile = async () => {
    try {
      const { data } = await adminAPI.updateRestaurantProfile(selectedRestaurant._id, profile);
      if (data.success) {
        alert('Profile saved');
        // Update local restaurant list
        setRestaurants(prev => prev.map(r => r._id === selectedRestaurant._id ? { ...r, ...profile } : r));
        setSelectedRestaurant(prev => ({ ...prev, ...profile }));
      }
    } catch (err) { alert('Failed: ' + (err.response?.data?.message || err.message)); }
  };

  // ==========================================
  // STYLES
  // ==========================================
  const s = {
    page: { padding: '24px', background: '#f8fafc', minHeight: '100vh' },
    header: { fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' },
    card: { background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '16px' },
    input: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' },
    select: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' },
    btn: { padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
    btnPrimary: { background: '#3b82f6', color: '#fff' },
    btnSuccess: { background: '#10b981', color: '#fff' },
    btnDanger: { background: '#ef4444', color: '#fff' },
    btnOutline: { background: '#fff', border: '1px solid #d1d5db', color: '#374151' },
    tab: (active) => ({ padding: '10px 20px', borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px', background: active ? '#fff' : '#e5e7eb', color: active ? '#1f2937' : '#6b7280', borderBottom: active ? '2px solid #3b82f6' : 'none' }),
    label: { display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px', fontWeight: '500' },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' },
    badge: (color) => ({ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', color: '#fff', background: color }),
  };

  // ==========================================
  // RENDER: RESTAURANT SELECTOR
  // ==========================================
  if (!selectedRestaurant) {
    return (
      <div style={s.page}>
        <h1 style={s.header}>Restaurant Manager</h1>
        <div style={s.card}>
          <input
            type="text"
            placeholder="Search restaurants by name, phone, or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...s.input, marginBottom: '16px' }}
          />
          {filteredRestaurants.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No restaurants found</div>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {filteredRestaurants.map(r => (
                <div key={r._id} onClick={() => selectRestaurant(r)} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '15px' }}>{r.name}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>{r.address?.city || ''} {r.ownerPhone ? `| ${r.ownerPhone}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={s.badge(r.isOpen ? '#10b981' : '#ef4444')}>{r.isOpen ? 'Open' : 'Closed'}</span>
                    <span style={{ color: '#3b82f6', fontWeight: '600', fontSize: '13px' }}>Manage &rarr;</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER: MANAGEMENT INTERFACE
  // ==========================================
  return (
    <div style={s.page}>
      {/* Header with back button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
        <button onClick={() => setSelectedRestaurant(null)} style={{ ...s.btn, ...s.btnOutline }}>&larr; Back</button>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', margin: 0 }}>{selectedRestaurant.name}</h1>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>{selectedRestaurant.address?.city || ''}</div>
        </div>
        <button
          onClick={() => openRestaurantPanel(selectedRestaurant)}
          title="Open this restaurant's own panel, exactly as they see it"
          style={{ ...s.btn, marginLeft: 'auto', background: '#7c3aed', color: '#fff', fontWeight: 600 }}
        >🔓 Open Panel</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '0' }}>
        {['menu', 'coupons', 'settings', 'profile'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={s.tab(activeTab === tab)}>
            {tab === 'menu' ? 'Menu Items' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div style={s.card}>
        {/* MENU TAB */}
        {activeTab === 'menu' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select value={menuCategory} onChange={e => { setMenuCategory(e.target.value); setTimeout(() => loadMenu(selectedRestaurant._id), 100); }} style={s.select}>
                  <option value="">All Categories</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input placeholder="Search items..." value={menuSearch} onChange={e => setMenuSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadMenu(selectedRestaurant._id)} style={{ ...s.input, width: '200px' }} />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={openCatOrder} style={{ ...s.btn, background: '#8b5cf6', color: '#fff' }} title="Set the order categories appear in the customer app">🔃 Category Order</button>
                <button onClick={() => { setShowBulkPanel(true); setBulkRows(null); setBulkMsg(''); }} style={{ ...s.btn, background: '#16a34a', color: '#fff' }}>📋 Bulk Add</button>
                <button onClick={() => { setShowBulkPanel(true); setBulkText(''); setBulkMsg(''); loadCurrentMenuIntoBulk(); }} style={{ ...s.btn, background: '#2563eb', color: '#fff' }} title="Load the whole current menu into the bulk editor to edit any item inline">✏️ Load &amp; Edit Menu</button>
                <button onClick={() => { setEditingMenuItem(null); setMenuForm({ name: '', description: '', category: 'Main Course', price: '', isVegetarian: false, preparationTime: 20 }); setShowMenuForm(true); }} style={{ ...s.btn, ...s.btnPrimary }}>+ Add Item</button>
              </div>
            </div>

            {/* Category Display Order Panel */}
            {showCatOrder && (
              <div style={{ ...s.card, border: '2px solid #8b5cf6', marginBottom: '16px' }}>
                <h3 style={{ marginBottom: '4px' }}>🔃 Category Display Order</h3>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  Set the order menu categories appear in the customer app for <strong>{selectedRestaurant?.name}</strong>. Top of the list = shown first. Categories you don't order here fall to the bottom alphabetically. Takes effect immediately — <strong>no app update needed</strong>.
                </div>
                {catEdit.length === 0 ? (
                  <div style={{ color: '#9ca3af', fontSize: '13px' }}>No categories yet — add menu items first.</div>
                ) : (
                  <div style={{ maxWidth: '440px' }}>
                    {catEdit.map((c, i) => (
                      <div key={c} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '6px', background: '#fff' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px' }}><span style={{ color: '#9ca3af', marginRight: '8px' }}>{i + 1}.</span>{c}</span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => moveCat(i, -1)} disabled={i === 0} style={{ ...s.btn, ...s.btnOutline, padding: '2px 10px', opacity: i === 0 ? 0.4 : 1, cursor: i === 0 ? 'default' : 'pointer' }}>▲</button>
                          <button onClick={() => moveCat(i, 1)} disabled={i === catEdit.length - 1} style={{ ...s.btn, ...s.btnOutline, padding: '2px 10px', opacity: i === catEdit.length - 1 ? 0.4 : 1, cursor: i === catEdit.length - 1 ? 'default' : 'pointer' }}>▼</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button onClick={saveCategoryOrder} disabled={catOrderSaving || catEdit.length === 0} style={{ ...s.btn, ...s.btnPrimary }}>{catOrderSaving ? 'Saving…' : '💾 Save order'}</button>
                  <button onClick={() => setShowCatOrder(false)} style={{ ...s.btn, ...s.btnOutline }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Menu Form Modal */}
            {showMenuForm && (
              <div style={{ ...s.card, border: '2px solid #3b82f6', marginBottom: '16px' }}>
                <h3 style={{ marginBottom: '12px' }}>{editingMenuItem ? 'Edit Menu Item' : 'Add Menu Item'}</h3>
                <form onSubmit={handleMenuSubmit}>
                  <div style={s.grid2}>
                    <div><label style={s.label}>Name *</label><input required value={menuForm.name} onChange={e => setMenuForm({ ...menuForm, name: e.target.value })} style={s.input} /></div>
                    <div><label style={s.label}>Price (Rs) *</label><input required type="number" min="1" value={menuForm.price} onChange={e => setMenuForm({ ...menuForm, price: e.target.value })} style={s.input} /></div>
                  </div>
                  <div style={{ marginTop: '12px' }}><label style={s.label}>Description</label><textarea value={menuForm.description} onChange={e => setMenuForm({ ...menuForm, description: e.target.value })} style={{ ...s.input, height: '60px' }} /></div>
                  <div style={{ ...s.grid3, marginTop: '12px' }}>
                    <div><label style={s.label}>Category</label><input type="text" value={menuForm.category} onChange={e => setMenuForm({ ...menuForm, category: e.target.value })} list="arm-menu-cats" placeholder="Pick or type a custom category" maxLength={50} style={{ ...s.select, width: '100%' }} /><datalist id="arm-menu-cats">{CATEGORIES.map(c => <option key={c} value={c} />)}</datalist></div>
                    <div><label style={s.label}>Prep Time (min)</label><input type="number" value={menuForm.preparationTime} onChange={e => setMenuForm({ ...menuForm, preparationTime: e.target.value })} style={s.input} /></div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', paddingBottom: '4px' }}>
                      <label><input type="checkbox" checked={menuForm.isVegetarian} onChange={e => setMenuForm({ ...menuForm, isVegetarian: e.target.checked })} /> Vegetarian</label>
                    </div>
                  </div>
                  {!editingMenuItem && (
                    <div style={{ marginTop: '12px' }}><label style={s.label}>Image</label><input type="file" accept="image/*" ref={menuImageRef} /></div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <button type="submit" style={{ ...s.btn, ...s.btnPrimary }}>{editingMenuItem ? 'Update' : 'Add Item'}</button>
                    <button type="button" onClick={() => { setShowMenuForm(false); setEditingMenuItem(null); }} style={{ ...s.btn, ...s.btnOutline }}>Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {/* Bulk Add Panel */}
            {showBulkPanel && (
              <div style={{ ...s.card, border: '2px solid #16a34a', marginBottom: '16px' }}>
                <h3 style={{ marginBottom: '8px' }}>📋 Bulk Add Menu Items</h3>
                {!bulkRows ? (
                  <>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                      One item per line, fields separated by <code style={{ background: '#f1f5f9', padding: '1px 4px' }}>|</code>. A number = price, <code style={{ background: '#f1f5f9', padding: '1px 4px' }}>Name:Price</code> = variants, and any plain text = the <strong>category</strong> (optional, any position). E.g. <code style={{ background: '#f1f5f9', padding: '1px 4px' }}>Name | Price | Category</code> · <code style={{ background: '#f1f5f9', padding: '1px 4px' }}>Name | Price | Variant:Price, ... | Category</code>.<br />
                      Descriptions are auto-fetched from the web; Veg is auto-detected. Category is auto-detected too — <strong>unless you give one</strong>, which then wins. You review/edit everything before adding.
                    </div>
                    <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} placeholder={'Paneer Tikka | 250 | Starters\nChicken Biryani | 220 | Half:140, Full:260 | Main Course\nMushroom Roll | Single:130, Double:150 | Snacks'} style={{ ...s.input, height: '160px', fontFamily: 'monospace', width: '100%' }} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px', fontSize: '13px', color: '#374151' }}>
                      <input type="checkbox" checked={bulkGenImages} onChange={e => setBulkGenImages(e.target.checked)} />
                      🖼️ Also generate AI food images (slower; representative, not the actual dish; small per-image cost)
                    </label>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                      <button onClick={runBulkScrape} disabled={bulkBusy || !bulkText.trim()} style={{ ...s.btn, ...s.btnPrimary }}>{bulkBusy ? (bulkGenImages ? 'Generating (images take longer)…' : 'Generating…') : 'Generate Preview'}</button>
                      <button onClick={loadCurrentMenuIntoBulk} disabled={bulkBusy} style={{ ...s.btn, background: '#2563eb', color: '#fff' }} title="Load the whole current menu here to edit existing items">{bulkBusy ? 'Loading…' : '📥 Load current menu to edit'}</button>
                      <button onClick={() => { setShowBulkPanel(false); setBulkText(''); setBulkMsg(''); }} style={{ ...s.btn, ...s.btnOutline }}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                      Review &amp; edit. {bulkRows.filter(r => r.existing).length} existing (blue — saved as <strong>updates</strong> in place), {bulkRows.filter(r => !r.existing && !r.alreadyExists).length} new. {bulkRows.filter(r => r.alreadyExists).length} already on the menu (amber — skipped); rows highlighted red have errors and won't be saved. <strong>Variants</strong> and per-item <strong>timing</strong> are editable here too.
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                            {['Name', 'Image', 'Description', 'Category', 'Veg', 'Price', 'Variants', 'Timing', ''].map((h, hi) => <th key={hi} style={{ textAlign: 'left', padding: '8px', fontSize: '12px', color: '#6b7280' }}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {bulkRows.map((r, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: r.errors?.length ? '#fef2f2' : (r.existing ? '#eff6ff' : (r.alreadyExists ? '#fffbeb' : '#fff')) }}>
                              <td style={{ padding: '6px' }}><input value={r.name} onChange={e => updateBulkRow(i, { name: e.target.value })} style={{ ...s.input, minWidth: '110px' }} /></td>
                              <td style={{ padding: '6px', textAlign: 'center' }}>
                                {r.image
                                  ? <div><img src={r.image} alt="" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px' }} /><div onClick={() => updateBulkRow(i, { image: '' })} style={{ fontSize: '10px', color: '#dc2626', cursor: 'pointer' }}>drop</div></div>
                                  : <span style={{ color: '#c0c0c0', fontSize: '11px' }}>—</span>}
                              </td>
                              <td style={{ padding: '6px' }}><textarea value={r.description} onChange={e => updateBulkRow(i, { description: e.target.value })} placeholder="(no web description — type one)" style={{ ...s.input, minWidth: '230px', height: '48px' }} /></td>
                              <td style={{ padding: '6px' }}><input type="text" value={r.category} onChange={e => updateBulkRow(i, { category: e.target.value })} list="arm-menu-cats" maxLength={50} style={s.select} /></td>
                              <td style={{ padding: '6px', textAlign: 'center' }}><input type="checkbox" checked={!!r.isVegetarian} onChange={e => updateBulkRow(i, { isVegetarian: e.target.checked })} /></td>
                              <td style={{ padding: '6px' }}><input type="number" min="1" value={r.price} onChange={e => updateBulkRow(i, { price: e.target.value })} style={{ ...s.input, width: '78px' }} /></td>
                              <td style={{ padding: '6px', fontSize: '12px', color: '#6b7280', minWidth: '190px' }}>
                                {(r.variants || []).map((v, vi) => (
                                  <div key={vi} style={{ display: 'flex', gap: '4px', marginBottom: '3px', alignItems: 'center' }}>
                                    <input value={v.name} onChange={e => updateBulkRow(i, { variants: r.variants.map((x, k) => k === vi ? { ...x, name: e.target.value } : x) })} placeholder="size/option" style={{ ...s.input, width: '86px', padding: '2px 4px', fontSize: '12px' }} />
                                    <input type="number" min="0" value={v.price} onChange={e => updateBulkRow(i, { variants: r.variants.map((x, k) => k === vi ? { ...x, price: e.target.value } : x) })} placeholder="₹" style={{ ...s.input, width: '58px', padding: '2px 4px', fontSize: '12px' }} />
                                    <button onClick={() => updateBulkRow(i, { variants: r.variants.filter((_, k) => k !== vi) })} title="Remove variant" style={{ ...s.btn, ...s.btnDanger, fontSize: '10px', padding: '1px 5px' }}>✕</button>
                                  </div>
                                ))}
                                <button onClick={() => updateBulkRow(i, { variants: [...(r.variants || []), { name: '', price: '' }] })} style={{ ...s.btn, fontSize: '11px', padding: '2px 6px', background: '#e5e7eb', color: '#374151' }}>+ variant</button>
                                {r.errors?.length ? <div style={{ color: '#dc2626' }}>{r.errors.join('; ')}</div> : null}
                                {r.existing ? <div style={{ color: '#2563eb', marginTop: '3px' }}>✎ existing — will be updated</div> : (r.alreadyExists ? <div style={{ color: '#b45309', marginTop: '3px' }}>⚠ already on menu — will be skipped</div> : null)}
                              </td>
                              <td style={{ padding: '6px', fontSize: '12px', color: '#6b7280', minWidth: '150px' }}>
                                {(() => {
                                  const t = r.availabilityTiming || { enabled: false, startTime: '00:00', endTime: '23:59' };
                                  const patchT = (patch) => updateBulkRow(i, { availabilityTiming: { ...t, ...patch } });
                                  return (
                                    <>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer' }} title="Only available during a time window each day">
                                        <input type="checkbox" checked={!!t.enabled} onChange={e => patchT({ enabled: e.target.checked })} /> timed
                                      </label>
                                      {t.enabled && (
                                        <div style={{ display: 'flex', gap: '3px', marginTop: '3px', alignItems: 'center' }}>
                                          <input type="time" value={t.startTime || '00:00'} onChange={e => patchT({ startTime: e.target.value })} style={{ ...s.input, width: '86px', padding: '2px 3px', fontSize: '12px' }} />
                                          <span style={{ fontSize: '11px' }}>–</span>
                                          <input type="time" value={t.endTime || '23:59'} onChange={e => patchT({ endTime: e.target.value })} style={{ ...s.input, width: '86px', padding: '2px 3px', fontSize: '12px' }} />
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </td>
                              <td style={{ padding: '6px', textAlign: 'center' }}><button onClick={() => removeBulkRow(i)} style={{ ...s.btn, ...s.btnDanger, fontSize: '11px', padding: '2px 6px' }}>✕</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                      <button onClick={runBulkCreate} disabled={bulkBusy} style={{ ...s.btn, ...s.btnPrimary }}>{bulkBusy ? 'Saving…' : (() => { const c = bulkSaveCounts(); const seg = []; if (c.updates) seg.push(`${c.updates} update`); if (c.creates) seg.push(`${c.creates} new`); return seg.length ? `✅ Save (${seg.join(' · ')})` : '✅ Save'; })()}</button>
                      <button onClick={() => setBulkRows(null)} style={{ ...s.btn, ...s.btnOutline }}>← Edit list</button>
                      <button onClick={() => { setShowBulkPanel(false); setBulkRows(null); setBulkText(''); setBulkMsg(''); }} style={{ ...s.btn, ...s.btnOutline }}>Cancel</button>
                    </div>
                  </>
                )}
                {bulkMsg && <div style={{ marginTop: '8px', fontSize: '13px', color: '#dc2626' }}>{bulkMsg}</div>}
              </div>
            )}

            {/* Menu Items List */}
            {menuLoading ? <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Item</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Category</th>
                    <th style={{ textAlign: 'right', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Price</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Status</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '13px', color: '#6b7280' }} title="Pinned 'Top Picks' strip at the top of the restaurant in the app">Top Pick</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {menuItems.map(item => (
                    <tr key={item._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {item.image && <img src={item.image} alt="" style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} />}
                          <div>
                            <div style={{ fontWeight: '600' }}>{item.isVegetarian ? '🟢' : '🔴'} {item.name}</div>
                            {item.description && <div style={{ fontSize: '12px', color: '#6b7280', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px', fontSize: '13px' }}>{item.category}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>Rs {item.price}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <span style={s.badge(item.isAvailable ? '#10b981' : '#ef4444')}>{item.isAvailable ? 'Available' : 'Unavailable'}</span>
                        {item.outOfStock?.isOutOfStock && <span style={{ ...s.badge('#f59e0b'), marginLeft: '4px' }}>Out of Stock</span>}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {(() => {
                          const tp = item.topPick || {};
                          const on = !!tp.enabled;
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                              <button
                                onClick={() => setTopPick(item._id, { enabled: !on, badge: tp.badge || 'bestseller', order: tp.order || 0 })}
                                style={{ ...s.btn, fontSize: '12px', padding: '4px 8px', background: on ? '#f59e0b' : '#e5e7eb', color: on ? '#fff' : '#374151' }}
                                title={on ? 'Remove from Top Picks' : 'Add to Top Picks (pinned at top in the app)'}
                              >
                                {on ? '⭐ Top Pick' : 'Add'}
                              </button>
                              {on && (
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <select
                                    value={tp.badge || 'bestseller'}
                                    onChange={e => setTopPick(item._id, { enabled: true, badge: e.target.value, order: tp.order || 0 })}
                                    style={{ ...s.select, fontSize: '11px', padding: '2px 4px' }}
                                    title="Badge shown on the item"
                                  >
                                    <option value="bestseller">🔥 Bestseller</option>
                                    <option value="deal">🏷️ Deal</option>
                                  </select>
                                  <input
                                    type="number" min="0" value={tp.order ?? 0}
                                    onChange={e => setTopPick(item._id, { enabled: true, badge: tp.badge || 'bestseller', order: e.target.value })}
                                    style={{ ...s.input, width: '48px', fontSize: '11px', padding: '2px 4px' }}
                                    title="Order in the strip (lower = shown first)"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button onClick={() => { setEditingMenuItem(item); setMenuForm({ name: item.name, description: item.description || '', category: item.category, price: item.price, isVegetarian: item.isVegetarian || false, preparationTime: item.preparationTime || 20 }); setShowMenuForm(true); }} style={{ ...s.btn, ...s.btnOutline, fontSize: '12px', padding: '4px 8px' }}>Edit</button>
                          <button onClick={() => toggleAvailability(item._id)} style={{ ...s.btn, fontSize: '12px', padding: '4px 8px', background: item.isAvailable ? '#f59e0b' : '#10b981', color: '#fff' }}>{item.isAvailable ? 'Hide' : 'Show'}</button>
                          <button onClick={() => toggleOutOfStock(item._id)} style={{ ...s.btn, fontSize: '12px', padding: '4px 8px', background: item.outOfStock?.isOutOfStock ? '#3b82f6' : '#6b7280', color: '#fff' }}>{item.outOfStock?.isOutOfStock ? 'In Stock' : 'Out of Stock'}</button>
                          <button onClick={() => deleteMenuItem(item._id)} style={{ ...s.btn, ...s.btnDanger, fontSize: '12px', padding: '4px 8px' }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!menuLoading && menuItems.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No menu items found</div>}
          </div>
        )}

        {/* COUPONS TAB */}
        {activeTab === 'coupons' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Coupons ({coupons.length})</h3>
              <button onClick={() => { setEditingCoupon(null); setCouponForm({ code: '', description: '', discountType: 'percentage', discountValue: '', minOrderValue: '', maxDiscount: '', validFrom: '', validUntil: '', usageLimit: '' }); setShowCouponForm(true); }} style={{ ...s.btn, ...s.btnPrimary }}>+ Create Coupon</button>
            </div>

            {showCouponForm && (
              <div style={{ ...s.card, border: '2px solid #3b82f6', marginBottom: '16px' }}>
                <h3 style={{ marginBottom: '12px' }}>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</h3>
                <form onSubmit={handleCouponSubmit}>
                  <div style={s.grid2}>
                    <div><label style={s.label}>Code *</label><input required value={couponForm.code} onChange={e => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} style={s.input} placeholder="e.g. SAVE20" /></div>
                    <div><label style={s.label}>Discount Type</label><select value={couponForm.discountType} onChange={e => setCouponForm({ ...couponForm, discountType: e.target.value })} style={{ ...s.select, width: '100%' }}><option value="percentage">Percentage (%)</option><option value="fixed">Fixed (Rs)</option></select></div>
                  </div>
                  <div style={{ marginTop: '12px' }}><label style={s.label}>Description *</label><input required value={couponForm.description} onChange={e => setCouponForm({ ...couponForm, description: e.target.value })} style={s.input} /></div>
                  <div style={{ ...s.grid3, marginTop: '12px' }}>
                    <div><label style={s.label}>Discount Value *</label><input required type="number" min="1" value={couponForm.discountValue} onChange={e => setCouponForm({ ...couponForm, discountValue: e.target.value })} style={s.input} /></div>
                    <div><label style={s.label}>Min Order Value</label><input type="number" value={couponForm.minOrderValue} onChange={e => setCouponForm({ ...couponForm, minOrderValue: e.target.value })} style={s.input} /></div>
                    {couponForm.discountType === 'percentage' && <div><label style={s.label}>Max Discount</label><input type="number" value={couponForm.maxDiscount} onChange={e => setCouponForm({ ...couponForm, maxDiscount: e.target.value })} style={s.input} /></div>}
                  </div>
                  <div style={{ ...s.grid3, marginTop: '12px' }}>
                    <div><label style={s.label}>Valid From</label><input type="date" value={couponForm.validFrom} onChange={e => setCouponForm({ ...couponForm, validFrom: e.target.value })} style={s.input} /></div>
                    <div><label style={s.label}>Valid Until *</label><input required type="date" value={couponForm.validUntil} onChange={e => setCouponForm({ ...couponForm, validUntil: e.target.value })} style={s.input} /></div>
                    <div><label style={s.label}>Usage Limit (blank = unlimited)</label><input type="number" value={couponForm.usageLimit} onChange={e => setCouponForm({ ...couponForm, usageLimit: e.target.value })} style={s.input} /></div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <button type="submit" style={{ ...s.btn, ...s.btnPrimary }}>{editingCoupon ? 'Update' : 'Create'}</button>
                    <button type="button" onClick={() => { setShowCouponForm(false); setEditingCoupon(null); }} style={{ ...s.btn, ...s.btnOutline }}>Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {couponLoading ? <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Code</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Description</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Discount</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Valid Until</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Usage</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Status</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '13px', color: '#6b7280' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map(c => {
                    const expired = c.validUntil && new Date(c.validUntil) < new Date();
                    return (
                      <tr key={c._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px' }}><code style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>{c.code}</code></td>
                        <td style={{ padding: '10px', fontSize: '13px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.description}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: '600' }}>{c.discountType === 'percentage' ? `${c.discountValue}%` : `Rs ${c.discountValue}`}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontSize: '13px' }}>{c.validUntil ? new Date(c.validUntil).toLocaleDateString() : '-'}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontSize: '13px' }}>{c.usedCount || 0}{c.usageLimit ? `/${c.usageLimit}` : ''}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <span style={s.badge(expired ? '#6b7280' : c.isActive ? '#10b981' : '#ef4444')}>{expired ? 'Expired' : c.isActive ? 'Active' : 'Inactive'}</span>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button onClick={() => { setEditingCoupon(c); setCouponForm({ code: c.code, description: c.description || '', discountType: c.discountType, discountValue: c.discountValue, minOrderValue: c.minOrderValue || '', maxDiscount: c.maxDiscount || '', validFrom: c.validFrom ? new Date(c.validFrom).toISOString().split('T')[0] : '', validUntil: c.validUntil ? new Date(c.validUntil).toISOString().split('T')[0] : '', usageLimit: c.usageLimit || '' }); setShowCouponForm(true); }} style={{ ...s.btn, ...s.btnOutline, fontSize: '12px', padding: '4px 8px' }}>Edit</button>
                            <button onClick={() => toggleCoupon(c._id)} style={{ ...s.btn, fontSize: '12px', padding: '4px 8px', background: c.isActive ? '#f59e0b' : '#10b981', color: '#fff' }}>{c.isActive ? 'Deactivate' : 'Activate'}</button>
                            <button onClick={() => deleteCoupon(c._id)} style={{ ...s.btn, ...s.btnDanger, fontSize: '12px', padding: '4px 8px' }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {!couponLoading && coupons.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No coupons found</div>}
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <div>
            {settingsLoading || !settings ? <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div> : (
              <>
                <h3 style={{ marginBottom: '16px' }}>Restaurant Settings</h3>

                {/* Toggles */}
                <div style={{ ...s.grid3, marginBottom: '20px' }}>
                  {[
                    { key: 'isOpen', label: 'Restaurant Open', icon: '🏪' },
                    { key: 'isAcceptingOrders', label: 'Accepting Orders', icon: '📦' },
                    { key: 'autoAcceptOrders', label: 'Auto Accept Orders', icon: '⚡' },
                  ].map(({ key, label, icon }) => (
                    <div key={key} style={{ padding: '16px', background: settings[key] ? '#f0fdf4' : '#fef2f2', borderRadius: '10px', border: `1px solid ${settings[key] ? '#86efac' : '#fca5a5'}` }}>
                      <div style={{ fontSize: '20px', marginBottom: '8px' }}>{icon}</div>
                      <div style={{ fontWeight: '600', marginBottom: '8px' }}>{label}</div>
                      <label style={{ cursor: 'pointer' }}>
                        <input type="checkbox" checked={settings[key] ?? true} onChange={e => setSettings({ ...settings, [key]: e.target.checked })} style={{ marginRight: '6px' }} />
                        {settings[key] ? 'Enabled' : 'Disabled'}
                      </label>
                    </div>
                  ))}
                </div>

                {/* Opening Hours */}
                <h4 style={{ marginBottom: '12px' }}>Opening Hours</h4>
                <div style={{ marginBottom: '20px' }}>
                  {DAYS.map(day => {
                    const hours = settings.openingHours?.[day] || { open: '09:00', close: '22:00', isOff: false };
                    return (
                      <div key={day} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ width: '100px', fontWeight: '600', textTransform: 'capitalize' }}>{day}</div>
                        <label style={{ fontSize: '13px' }}>
                          <input type="checkbox" checked={!hours.isOff} onChange={e => setSettings({ ...settings, openingHours: { ...settings.openingHours, [day]: { ...hours, isOff: !e.target.checked } } })} /> Open
                        </label>
                        {!hours.isOff && (
                          <>
                            <input type="time" value={hours.open || '09:00'} onChange={e => setSettings({ ...settings, openingHours: { ...settings.openingHours, [day]: { ...hours, open: e.target.value } } })} style={{ ...s.input, width: '130px' }} />
                            <span>to</span>
                            <input type="time" value={hours.close || '22:00'} onChange={e => setSettings({ ...settings, openingHours: { ...settings.openingHours, [day]: { ...hours, close: e.target.value } } })} style={{ ...s.input, width: '130px' }} />
                          </>
                        )}
                        {hours.isOff && <span style={{ color: '#ef4444', fontSize: '13px' }}>Closed</span>}
                      </div>
                    );
                  })}
                </div>

                {/* Delivery Settings */}
                <h4 style={{ marginBottom: '12px' }}>Delivery Settings</h4>
                <div style={s.grid3}>
                  <div><label style={s.label}>Preparation Time (min)</label><input type="number" value={settings.deliveryTimings?.preparationTime || 30} onChange={e => setSettings({ ...settings, deliveryTimings: { ...settings.deliveryTimings, preparationTime: Number(e.target.value) } })} style={s.input} /></div>
                  <div><label style={s.label}>Delivery Radius (km)</label><input type="number" value={settings.deliveryTimings?.deliveryRadius || 10} onChange={e => setSettings({ ...settings, deliveryTimings: { ...settings.deliveryTimings, deliveryRadius: Number(e.target.value) } })} style={s.input} /></div>
                  <div><label style={s.label}>Est. Delivery Time (min)</label><input type="number" value={settings.deliveryTimings?.estimatedDeliveryTime || 45} onChange={e => setSettings({ ...settings, deliveryTimings: { ...settings.deliveryTimings, estimatedDeliveryTime: Number(e.target.value) } })} style={s.input} /></div>
                </div>

                <button onClick={saveSettings} style={{ ...s.btn, ...s.btnSuccess, marginTop: '20px', padding: '12px 24px' }}>Save Settings</button>
              </>
            )}
          </div>
        )}

        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <div>
            <h3 style={{ marginBottom: '16px' }}>Restaurant Profile</h3>
            <div style={s.grid2}>
              <div><label style={s.label}>Restaurant Name</label><input value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} style={s.input} /></div>
              <div><label style={s.label}>Phone</label><input value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} style={s.input} /></div>
            </div>
            <div style={{ marginTop: '12px' }}><label style={s.label}>Description</label><textarea value={profile.description} onChange={e => setProfile({ ...profile, description: e.target.value })} style={{ ...s.input, height: '80px' }} /></div>
            <div style={{ marginTop: '12px' }}><label style={s.label}>Email</label><input value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} style={s.input} /></div>

            <h4 style={{ marginTop: '20px', marginBottom: '12px' }}>Address</h4>
            <div style={s.grid2}>
              <div><label style={s.label}>Street</label><input value={profile.address?.street || ''} onChange={e => setProfile({ ...profile, address: { ...profile.address, street: e.target.value } })} style={s.input} /></div>
              <div><label style={s.label}>City</label><input value={profile.address?.city || ''} onChange={e => setProfile({ ...profile, address: { ...profile.address, city: e.target.value } })} style={s.input} /></div>
            </div>
            <div style={{ ...s.grid2, marginTop: '12px' }}>
              <div><label style={s.label}>State</label><input value={profile.address?.state || ''} onChange={e => setProfile({ ...profile, address: { ...profile.address, state: e.target.value } })} style={s.input} /></div>
              <div><label style={s.label}>Pincode</label><input value={profile.address?.zipCode || profile.address?.pincode || ''} onChange={e => setProfile({ ...profile, address: { ...profile.address, zipCode: e.target.value } })} style={s.input} /></div>
            </div>

            <button onClick={saveProfile} style={{ ...s.btn, ...s.btnSuccess, marginTop: '20px', padding: '12px 24px' }}>Save Profile</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminRestaurantManager;
